// src/services/apiClient.js
//
// Single entry point for every authenticated call to the Vennio API
// (api.vennio.app). Replaces ~7 hand-rolled `getSession()` + `fetch` helpers
// that each forwarded the cached access token and had no recovery when it was
// stale — see the 2026-06-25 incident: a globally-revoked session left an open
// tab holding a clock-valid-but-dead token, so every request 401'd until the
// user manually signed out and back in.
//
// What this adds over the old per-service helpers:
//   1. On a 401, refresh the session ONCE and retry the request.
//   2. If the refresh also fails (the orphaned-session case), sign out cleanly
//      and let the app's ProtectedRoute bounce the user to /auth — no manual
//      sign-out needed. SIGNED_OUT propagates cross-tab, so stale tabs follow.
//   3. One place that attaches the bearer, parses errors, and handles 204.
//
// Error contract (unchanged from the old helpers so existing catch blocks keep
// working): throws an Error whose `.message` is body.detail || body.message ||
// body.title || `HTTP <status>`, with `.status` and the parsed `.body` attached.

import { supabase } from '../lib/supabase';
import { showError } from '../lib/toast';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

// Dedupe concurrent refreshes: if 5 requests 401 at once, refresh once and let
// all of them retry against the new token.
let refreshPromise = null;
function refreshSessionOnce() {
  if (!refreshPromise) {
    refreshPromise = supabase.auth
      .refreshSession()
      .then(({ data, error }) => !error && !!data?.session)
      .catch(() => false)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

// Run exactly once per dead session: clear local auth state and toast. Setting
// the session to null fires SIGNED_OUT → AuthContext nulls the user →
// ProtectedRoute redirects to /auth. `scope: 'local'` avoids a doomed server
// round-trip to revoke an already-revoked session.
let sessionExpiredHandled = false;
async function handleSessionExpired() {
  if (sessionExpiredHandled) return;
  sessionExpiredHandled = true;
  try {
    await supabase.auth.signOut({ scope: 'local' });
  } catch {
    // already gone — the SIGNED_OUT broadcast still fires
  }
  showError('Your session expired — please sign in again.');
}

async function buildError(res) {
  let body = null;
  try {
    body = await res.json();
  } catch {
    // non-JSON (HTML 502/504, empty body) — fall back to status
  }
  const message =
    body?.detail || body?.message || body?.title || `HTTP ${res.status}`;
  const err = new Error(message);
  err.status = res.status;
  err.body = body;
  return err;
}

/**
 * Authenticated fetch against the Vennio API with automatic 401 recovery.
 *
 * @param {string} path - API path beginning with `/` (e.g. `/v1/proposals`)
 * @param {Object} [options]
 * @param {string} [options.method='GET']
 * @param {Object} [options.body] - JSON-serialisable body; sets Content-Type
 * @param {boolean} [options.auth=true] - attach the bearer token
 * @param {boolean} [options.raw=false] - resolve with the Response (for blobs)
 * @param {Object} [options.headers] - extra headers
 * @returns {Promise<any>} parsed JSON, `null` for empty/204, or Response if raw
 * @throws {Error} with `.status` and `.body` on non-OK responses
 */
export async function apiFetch(
  path,
  { method = 'GET', body, auth = true, raw = false, headers = {} } = {}
) {
  const doRequest = async () => {
    let authHeader = {};
    if (auth) {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        const err = new Error('Not authenticated');
        err.status = 401;
        throw err;
      }
      authHeader = { Authorization: `Bearer ${session.access_token}` };
    }
    const finalHeaders = {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...authHeader,
      ...headers,
    };
    return fetch(`${API_URL}${path}`, {
      method,
      headers: finalHeaders,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  };

  let res = await doRequest();

  // 401 recovery: refresh once, retry once. If still 401, the session is dead.
  if (auth && res.status === 401) {
    const refreshed = await refreshSessionOnce();
    if (refreshed) {
      res = await doRequest();
    }
    if (res.status === 401) {
      await handleSessionExpired();
      throw await buildError(res);
    }
  }

  if (!res.ok) {
    throw await buildError(res);
  }

  if (raw) return res;
  if (res.status === 204) return null;
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}
